'use client';

import {
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle
} from 'react';

import { styles } from './UiKitPanelPeek.styles';

export interface UiKitPanelPeekProps {
  /** Видимая высота панели в peek-состоянии */
  peekHeight: number;
  /** Максимальная высота панели (по умолчанию — viewport) */
  maxHeight?: number;
  children: ReactNode;
}

export interface UiKitPanelPeekRef {
  recalculate: () => void;
}

const HANDLE_HEIGHT = 24;

function useViewportHeight() {
  const [height, setHeight] = useState(
    typeof window !== 'undefined' ? window.innerHeight : 0
  );

  useEffect(() => {
    const onResize = () => setHeight(window.innerHeight);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return height;
}

export const UiKitPanelPeek = forwardRef<UiKitPanelPeekRef, UiKitPanelPeekProps>(
  function UiKitPanelPeek({ peekHeight, maxHeight, children }, ref) {
    const slots = styles();

    const viewportHeight = useViewportHeight();
    const maxPanelHeight = maxHeight ?? viewportHeight;

    const contentRef = useRef<HTMLDivElement>(null);
    const measureRef = useRef<HTMLDivElement>(null);

    const startYRef = useRef(0);
    const startOffsetRef = useRef(0);

    const offsetRef = useRef<number | null>(null);

    const [offset, setOffset] = useState(0);
    const [contentHeight, setContentHeight] = useState(0);
    const [isDragging, setIsDragging] = useState(false);

    const measure = () => {
      if (!measureRef.current) return;
      setContentHeight(measureRef.current.scrollHeight);
    };

    /**
     * ResizeObserver — только на measureRef
     */
    useEffect(() => {
      if (!measureRef.current) return;

      const observer = new ResizeObserver(() => {
        measure();
      });

      observer.observe(measureRef.current);
      measure();

      return () => observer.disconnect();
    }, []);

    useImperativeHandle(ref, () => ({
      recalculate: () => {
        requestAnimationFrame(() => {
          measure();
        });
      }
    }));

    /**
     * Реальная высота панели = контент + handle
     */
    const naturalHeight = contentHeight + HANDLE_HEIGHT;

    const panelHeight = Math.min(naturalHeight, maxPanelHeight);

    /**
     * Peek offset
     */
    const peekOffset = Math.max(panelHeight - peekHeight, 0);

    /**
     * Инициализация offset — строго один раз
     */
    useLayoutEffect(() => {
      if (offsetRef.current !== null) return;
      if (peekOffset <= 0) return;

      offsetRef.current = peekOffset;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setOffset(peekOffset);
    }, [peekOffset]);

    /**
     * Pointer handlers
     */
    const onPointerDown = (e: React.PointerEvent) => {
      if (offsetRef.current === null) return;

      setIsDragging(true);
      startYRef.current = e.clientY;
      startOffsetRef.current = offsetRef.current;

      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    };

    const onPointerMove = (e: React.PointerEvent) => {
      if (!isDragging) return;

      const delta = e.clientY - startYRef.current;
      const next = startOffsetRef.current + delta;

      const clamped = Math.min(Math.max(next, 0), peekOffset);

      offsetRef.current = clamped;
      setOffset(clamped);
    };

    const onPointerUp = () => {
      if (offsetRef.current === null) return;

      setIsDragging(false);

      const next = offsetRef.current < peekOffset / 2 ? 0 : peekOffset;

      offsetRef.current = next;
      setOffset(next);
    };

    /**
     * Clamp при уменьшении размеров
     */
    useEffect(() => {
      if (offsetRef.current === null) return;

      if (offsetRef.current > peekOffset) {
        offsetRef.current = peekOffset;
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setOffset(peekOffset);
      }
    }, [peekOffset]);

    return (
      <div
        className={slots.container()}
        style={{
          height: panelHeight,
          transform: `translateY(${offset}px)`,
          transition: isDragging ? 'none' : 'transform 0.25s ease-out',
          willChange: 'transform'
        }}
      >
        <div
          className={slots.handle()}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
        >
          <div className={slots.handleBar()} />
        </div>

        <div
          ref={contentRef}
          className={slots.content()}
        >
          <div
            ref={measureRef}
            className="relative"
          >
            {children}
          </div>
        </div>
      </div>
    );
  }
);
